import { useState } from 'react';
import { View } from 'foundations-components/transitional/components/View';
import { Text } from 'foundations-components/components/Text';
import { Flex } from 'foundations-components/components/Flex';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { H3 } from 'foundations-components/transitional/components/H3';
import { Paragraph } from 'foundations-components/transitional/components/Paragraph';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { Button } from 'foundations-components/transitional/components/Button';
import { CardWrapper, CardHeader, CardSection } from 'foundations-components/transitional/components/Card';
import { SideNav, SideNavItem } from 'foundations-components/transitional/components/SideNav';
import { useTheme } from 'foundations-theming/utils';
import { useSlowTestMethodQuery } from '../../__generated__/chirp/com/hubspot/portals/platform/reference/be/rpc/PortalRpc.hooks';
import { THEME_FIELD_SECTIONS, FONT_SECTIONS } from '../constants';
import { NavMarker } from 'react-rhumb';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
function TokenSwatch({
  token
}) {
  const theme = useTheme();
  const isBorderToken = token.startsWith('border-');
  const isTextToken = token.startsWith('text-');
  const isIconToken = token.startsWith('icon-');
  const hexValue = theme.color[token] || 'unknown';
  return /*#__PURE__*/_jsxs(View, {
    display: "inline-block",
    textAlign: "center",
    inlineSize: "160px",
    color: "text-core-default",
    children: [/*#__PURE__*/_jsx(View, {
      as: "div",
      backgroundColor: token,
      borderStyle: "solid",
      borderColor: isBorderToken ? token : 'border-core-default',
      borderWidth: isBorderToken ? '200' : '100',
      borderRadius: "100",
      inlineSize: "48px",
      blockSize: "48px",
      marginInline: "auto",
      marginBlockEnd: "200",
      alignItems: "center",
      justifyContent: "center",
      children: (isTextToken || isIconToken) && /*#__PURE__*/_jsx(Text, {
        color: token,
        typography: "body-100",
        children: "Aa"
      })
    }), /*#__PURE__*/_jsx(Text, {
      typography: "detail-100",
      children: token
    }), /*#__PURE__*/_jsx(Text, {
      typography: "detail-100",
      use: "help",
      children: hexValue
    })]
  });
}
function SourceTokenDisplay({
  token
}) {
  const theme = useTheme();
  const hexValue = theme.color[token] || 'unknown';
  return /*#__PURE__*/_jsxs(View, {
    marginBlockEnd: "600",
    children: [/*#__PURE__*/_jsx(Text, {
      typography: "body-100",
      use: "help",
      marginBlockEnd: "200",
      children: "SOURCE TOKEN"
    }), /*#__PURE__*/_jsxs(TFlex, {
      gap: 20,
      align: "center",
      children: [/*#__PURE__*/_jsx(View, {
        as: "div",
        backgroundColor: token,
        borderStyle: "solid",
        borderColor: "border-core-default",
        borderWidth: "100",
        borderRadius: "100",
        inlineSize: "72px",
        blockSize: "72px"
      }), /*#__PURE__*/_jsxs(View, {
        children: [/*#__PURE__*/_jsx(Text, {
          typography: "heading-300",
          children: token
        }), /*#__PURE__*/_jsx(Text, {
          typography: "body-100",
          use: "help",
          children: hexValue
        }), /*#__PURE__*/_jsx(Text, {
          typography: "detail-100",
          use: "help",
          marginBlockStart: "200",
          color: "text-core-subtle",
          children: "All tokens below are derived from this value"
        })]
      })]
    })]
  });
}
function TokenSection({
  config
}) {
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(H3, {
      marginBlockEnd: "400",
      children: config.title
    }), /*#__PURE__*/_jsx(Paragraph, {
      marginBlockEnd: "400",
      children: config.description
    }), /*#__PURE__*/_jsx(CardWrapper, {
      borderRadius: "0",
      children: /*#__PURE__*/_jsxs(CardSection, {
        children: [/*#__PURE__*/_jsx(SourceTokenDisplay, {
          token: config.sourceToken
        }), /*#__PURE__*/_jsx(TFlex, {
          gap: 20,
          wrap: "wrap",
          paddingBlockStart: "800",
          children: config.derivedTokens.map(token => /*#__PURE__*/_jsx(TokenSwatch, {
            token: token
          }, token))
        })]
      })
    })]
  });
}
function FontTypographySwatch({
  token
}) {
  const theme = useTheme();
  const typography = theme.typography[token];
  const fontSize = (typography === null || typography === void 0 ? void 0 : typography.fontSize) || '—';
  const fontWeight = (typography === null || typography === void 0 ? void 0 : typography.fontWeight) || '—';
  return /*#__PURE__*/_jsxs(TFlex, {
    align: "center",
    paddingBlock: "400",
    borderBlockEndStyle: "solid",
    borderBlockEndWidth: "100",
    borderBlockEndColor: "border-core-subtle",
    children: [/*#__PURE__*/_jsxs(View, {
      inlineSize: "200px",
      flexShrink: "0",
      children: [/*#__PURE__*/_jsx(Text, {
        typography: "body-100",
        children: token
      }), /*#__PURE__*/_jsxs(Text, {
        typography: "detail-100",
        use: "help",
        children: [fontSize, " / ", fontWeight]
      })]
    }), /*#__PURE__*/_jsx(View, {
      flexGrow: "1",
      children: /*#__PURE__*/_jsx(Text, {
        typography: token,
        children: "The quick brown fox"
      })
    })]
  });
}
function FontSourceDisplay({
  referenceToken
}) {
  const theme = useTheme();
  const typography = theme.typography[referenceToken];
  const fontFamily = (typography === null || typography === void 0 ? void 0 : typography.fontFamily) || 'unknown';
  const primaryFontName = fontFamily.split(',')[0].trim();
  return /*#__PURE__*/_jsxs(View, {
    marginBlockEnd: "600",
    children: [/*#__PURE__*/_jsx(Text, {
      typography: "body-100",
      use: "help",
      marginBlockEnd: "200",
      children: "SOURCE FONT"
    }), /*#__PURE__*/_jsxs(View, {
      children: [/*#__PURE__*/_jsx(Text, {
        typography: "heading-300",
        children: primaryFontName
      }), /*#__PURE__*/_jsx(Text, {
        typography: "detail-100",
        use: "help",
        marginBlockStart: "200",
        color: "text-core-subtle",
        children: "All typography below uses this font family"
      })]
    })]
  });
}
function FontSection({
  config
}) {
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(H3, {
      marginBlockEnd: "400",
      children: config.title
    }), /*#__PURE__*/_jsx(Paragraph, {
      marginBlockEnd: "400",
      children: config.description
    }), /*#__PURE__*/_jsx(CardWrapper, {
      borderRadius: "0",
      children: /*#__PURE__*/_jsxs(CardSection, {
        children: [/*#__PURE__*/_jsx(FontSourceDisplay, {
          referenceToken: config.referenceToken
        }), /*#__PURE__*/_jsx(View, {
          paddingBlockStart: "600",
          children: config.typographyTokens.map(token => /*#__PURE__*/_jsx(FontTypographySwatch, {
            token: token
          }, token))
        })]
      })
    })]
  });
}
export default function TokenExplorer() {
  const [activeSection, setActiveSection] = useState(THEME_FIELD_SECTIONS[0].id);
  const [isSlowTestEnabled, setIsSlowTestEnabled] = useState(false);
  const {
    data: slowTestData,
    loading: isSlowTestLoading,
    error: slowTestError
  } = useSlowTestMethodQuery({
    variables: {
      requestField: 'slow-test',
      domain: '',
      cookie: ''
    },
    skip: !isSlowTestEnabled,
    context: {
      timeout: 15000
    }
  });
  console.log('CHIRP Proxy: slowTestMethod', {
    enabled: isSlowTestEnabled,
    loading: isSlowTestLoading,
    data: slowTestData,
    error: slowTestError
  });
  const scrollToSection = sectionId => {
    var _document$getElementB;
    setActiveSection(sectionId);
    (_document$getElementB = document.getElementById(sectionId)) === null || _document$getElementB === void 0 || _document$getElementB.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return /*#__PURE__*/_jsx(NavMarker, {
    name: "INDEX_LOAD",
    children: /*#__PURE__*/_jsxs(TFlex, {
      direction: "row",
      children: [/*#__PURE__*/_jsx(View, {
        minInlineSize: "220px",
        maxInlineSize: "220px",
        paddingBlock: "600",
        paddingInline: "400",
        position: "sticky",
        insetBlockStart: "0",
        alignSelf: "start",
        children: /*#__PURE__*/_jsxs(SideNav
        // @ts-expect-error - color is excluded from type but we need it
        // for proper light/dark mode support
        , {
          color: "text-core-default",
          value: activeSection,
          headingText: "Theme Fields",
          "aria-label": "Theme Fields navigation",
          children: [THEME_FIELD_SECTIONS.map(section => /*#__PURE__*/_jsx(SideNavItem, {
            value: section.id,
            title: section.title,
            onClick: () => scrollToSection(section.id)
          }, section.id)), FONT_SECTIONS.map(section => /*#__PURE__*/_jsx(SideNavItem, {
            value: section.id,
            title: section.title,
            onClick: () => scrollToSection(section.id)
          }, section.id))]
        })
      }), /*#__PURE__*/_jsxs(View, {
        flex: "1",
        padding: "600",
        color: "text-core-default",
        children: [/*#__PURE__*/_jsxs(CardWrapper, {
          marginBlockEnd: "800",
          children: [/*#__PURE__*/_jsx(CardHeader, {
            title: "Slow Endpoint Test"
          }), /*#__PURE__*/_jsx(CardSection, {
            children: /*#__PURE__*/_jsxs(Flex, {
              flexDirection: "column",
              gap: "300",
              children: [/*#__PURE__*/_jsx(Text, {
                typography: "body-200",
                children: "Calls slowTestMethod which sleeps 10 seconds before responding. Client timeout set to 15 seconds."
              }), /*#__PURE__*/_jsxs(Flex, {
                gap: "400",
                alignItems: "center",
                children: [/*#__PURE__*/_jsx(Button, {
                  use: "secondary",
                  size: "small",
                  disabled: isSlowTestLoading,
                  onClick: () => setIsSlowTestEnabled(true),
                  children: isSlowTestLoading ? 'Waiting...' : 'Call Slow Endpoint'
                }), isSlowTestLoading && /*#__PURE__*/_jsx(Text, {
                  typography: "body-100",
                  color: "text-core-subtle",
                  children: "Request in flight..."
                })]
              }), slowTestError && /*#__PURE__*/_jsx(Alert, {
                type: "danger",
                children: /*#__PURE__*/_jsx(Text, {
                  typography: "body-200",
                  children: slowTestError.message || 'Request failed'
                })
              }), slowTestData && /*#__PURE__*/_jsx(Alert, {
                type: "success",
                children: /*#__PURE__*/_jsxs(Text, {
                  typography: "body-200",
                  children: ["Response: ", slowTestData.responseField]
                })
              })]
            })
          })]
        }), THEME_FIELD_SECTIONS.map(section => /*#__PURE__*/_jsx(View, {
          id: section.id,
          marginBlockEnd: "800",
          children: /*#__PURE__*/_jsx(TokenSection, {
            config: section
          })
        }, section.id)), FONT_SECTIONS.map(section => /*#__PURE__*/_jsx(View, {
          id: section.id,
          marginBlockEnd: "800",
          children: /*#__PURE__*/_jsx(FontSection, {
            config: section
          })
        }, section.id))]
      })]
    })
  });
}